// -----------------------------------------------------------
// FloraIQ - Memorial Descritivo da Plataforma (Página Principal)
// -----------------------------------------------------------

import { useState, useRef, useCallback, useEffect } from "react";
import PageHeader from "@/components/shared/PageHeader";
import {
  memorialOverview,
  memorialArchitecture,
  memorialModules,
  memorialMethodology,
  memorialTechStack,
  memorialStandards,
  memorialGlossary,
  memorialReleases,
} from "@/mocks/memorial";

const sections = [
  { id: "visao-geral", label: "Visão Geral", icon: "ri-eye-line" },
  { id: "arquitetura", label: "Arquitetura Técnica", icon: "ri-server-line" },
  { id: "modulos", label: "Módulos do Sistema", icon: "ri-apps-line" },
  { id: "metodologia", label: "Metodologia LADM", icon: "ri-node-tree" },
  { id: "tecnologias", label: "Stack Tecnológico", icon: "ri-code-box-line" },
  { id: "padroes", label: "Padrões e Normas", icon: "ri-shield-check-line" },
  { id: "glossario", label: "Glossário Técnico", icon: "ri-book-open-line" },
  { id: "releases", label: "Versionamento", icon: "ri-git-commit-line" },
];

export default function MemorialDescritivoPage() {
  const [activeSection, setActiveSection] = useState("visao-geral");
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [expandedArch, setExpandedArch] = useState<string | null>(null);
  const [expandedRelease, setExpandedRelease] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const scrollToSection = useCallback((id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
    }
  }, []);

  // IntersectionObserver para highlight do sumário
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const filteredGlossary = searchTerm
    ? memorialGlossary.filter(
        (g) =>
          g.termo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          g.definicao.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : memorialGlossary;

  return (
    <div className="flex gap-6 relative">
      {/* Main Content */}
      <div className="flex-1 min-w-0 space-y-8">
        <PageHeader
          title="Memorial Descritivo da Plataforma"
          subtitle={`${memorialOverview.titulo} — ${memorialOverview.versao} — Atualizado em ${memorialOverview.dataAtualizacao}`}
        >
          <button
            onClick={() => scrollToSection("visao-geral")}
            className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-up-line mr-1"></i>
            Topo
          </button>
          <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
            <i className="ri-download-line mr-1"></i>
            <span className="hidden sm:inline">Exportar PDF</span>
          </button>
          <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
            <i className="ri-file-copy-line mr-1"></i>
            <span className="hidden sm:inline">Copiar Link</span>
          </button>
        </PageHeader>

        {/* === VISÃO GERAL === */}
        <section
          id="visao-geral"
          ref={(el) => { sectionRefs.current["visao-geral"] = el; }}
          className="scroll-mt-24"
        >
          <div className="bg-white rounded-card border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-eye-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-900 font-heading">Visão Geral da Plataforma</h2>
                <p className="text-xs text-foreground-500">Contexto, objetivo e escopo do sistema</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-background-50 rounded-lg p-4 border border-background-200/50">
                <p className="text-xs text-foreground-500 mb-1">Organização</p>
                <p className="text-sm font-medium text-foreground-800">{memorialOverview.organizacao}</p>
              </div>
              <div className="bg-background-50 rounded-lg p-4 border border-background-200/50">
                <p className="text-xs text-foreground-500 mb-1">Responsável Técnico</p>
                <p className="text-sm font-medium text-foreground-800">{memorialOverview.responsavel}</p>
              </div>
              <div className="bg-background-50 rounded-lg p-4 border border-background-200/50">
                <p className="text-xs text-foreground-500 mb-1">Versão Atual</p>
                <p className="text-sm font-medium text-foreground-800">{memorialOverview.versao}</p>
              </div>
              <div className="bg-background-50 rounded-lg p-4 border border-background-200/50">
                <p className="text-xs text-foreground-500 mb-1">Última Atualização</p>
                <p className="text-sm font-medium text-foreground-800">{memorialOverview.dataAtualizacao}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">Descrição</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{memorialOverview.descricao}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">Objetivo Geral</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{memorialOverview.objetivoGeral}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">Escopo</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{memorialOverview.escopo}</p>
              </div>
            </div>
          </div>
        </section>

        {/* === ARQUITETURA TÉCNICA === */}
        <section
          id="arquitetura"
          ref={(el) => { sectionRefs.current["arquitetura"] = el; }}
          className="scroll-mt-24"
        >
          <div className="bg-white rounded-card border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                <i className="ri-server-line text-secondary-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-900 font-heading">Arquitetura Técnica</h2>
                <p className="text-xs text-foreground-500">Camadas, tecnologias e características do sistema</p>
              </div>
            </div>

            <div className="space-y-3">
              {memorialArchitecture.map((arch) => (
                <div
                  key={arch.camada}
                  className="border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedArch(expandedArch === arch.camada ? null : arch.camada)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-background-50/50 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center shrink-0">
                      <i className="ri-stack-line text-primary-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-800">{arch.camada}</h3>
                      <p className="text-xs text-foreground-500 truncate">{arch.descricao.slice(0, 80)}...</p>
                    </div>
                    <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${expandedArch === arch.camada ? "rotate-180" : ""}`}></i>
                  </button>
                  {expandedArch === arch.camada && (
                    <div className="px-4 pb-4 pt-0 space-y-4 slide-up">
                      <p className="text-sm text-foreground-600 leading-relaxed">{arch.descricao}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
                          <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-2">Tecnologias</p>
                          <div className="flex flex-wrap gap-1.5">
                            {arch.tecnologias.map((t) => (
                              <span key={t} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded text-[11px] font-medium whitespace-nowrap">{t}</span>
                            ))}
                          </div>
                        </div>
                        <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
                          <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-2">Características</p>
                          <ul className="space-y-1">
                            {arch.caracteristicas.map((c) => (
                              <li key={c} className="flex items-start gap-1.5 text-xs text-foreground-600">
                                <i className="ri-check-line text-primary-500 mt-0.5 shrink-0"></i>
                                <span>{c}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* === MÓDULOS === */}
        <section
          id="modulos"
          ref={(el) => { sectionRefs.current["modulos"] = el; }}
          className="scroll-mt-24"
        >
          <div className="bg-white rounded-card border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-apps-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-900 font-heading">Módulos do Sistema</h2>
                <p className="text-xs text-foreground-500">{memorialModules.length} módulos integrados — funcionalidades e público-alvo</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {memorialModules.map((mod) => (
                <div
                  key={mod.id}
                  className="border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-colors"
                >
                  <button
                    onClick={() => setExpandedModule(expandedModule === mod.id ? null : mod.id)}
                    className="w-full flex items-start gap-3 p-4 text-left hover:bg-background-50/50 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`${mod.icone} text-primary-600 text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-800">{mod.nome}</h3>
                      <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{mod.descricao}</p>
                    </div>
                    <i className={`ri-arrow-down-s-line text-foreground-400 mt-1 transition-transform ${expandedModule === mod.id ? "rotate-180" : ""}`}></i>
                  </button>
                  {expandedModule === mod.id && (
                    <div className="px-4 pb-4 pt-0 space-y-3 slide-up">
                      <p className="text-sm text-foreground-600 leading-relaxed">{mod.descricao}</p>
                      <div>
                        <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-2">Funcionalidades</p>
                        <ul className="space-y-1">
                          {mod.funcionalidades.map((f) => (
                            <li key={f} className="flex items-start gap-1.5 text-xs text-foreground-600">
                              <i className="ri-check-line text-primary-500 mt-0.5 shrink-0"></i>
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
                        <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-1">Público-alvo</p>
                        <p className="text-xs text-foreground-600">{mod.publicoAlvo}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* === METODOLOGIA LADM === */}
        <section
          id="metodologia"
          ref={(el) => { sectionRefs.current["metodologia"] = el; }}
          className="scroll-mt-24"
        >
          <div className="bg-white rounded-card border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-node-tree text-primary-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-900 font-heading">{memorialMethodology.titulo}</h2>
                <p className="text-xs text-foreground-500">Baseada na ISO 19152 — LADM adaptado para arborização urbana</p>
              </div>
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed mb-6">{memorialMethodology.descricao}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {memorialMethodology.pilares.map((p) => (
                <div key={p.nome} className="bg-background-50 rounded-lg p-4 border border-background-200/50">
                  <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center mb-3">
                    <i className={`${p.icone} text-primary-600 text-sm`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">{p.nome}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{p.descricao}</p>
                </div>
              ))}
            </div>

            <div className="bg-primary-50/50 border border-primary-200/50 rounded-lg p-4">
              <p className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
                <i className="ri-git-branch-line text-primary-500"></i>
                Fluxo Metodológico — 7 Etapas
              </p>
              <div className="flex flex-wrap gap-2">
                {memorialMethodology.fluxoEtapas.map((etapa, idx) => (
                  <div key={etapa} className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-primary-200 rounded-md text-xs font-medium text-primary-700 whitespace-nowrap">
                      <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-[10px] font-bold">{idx + 1}</span>
                      {etapa}
                    </span>
                    {idx < memorialMethodology.fluxoEtapas.length - 1 && (
                      <i className="ri-arrow-right-line text-primary-300 text-xs hidden sm:inline"></i>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* === STACK TECNOLÓGICO === */}
        <section
          id="tecnologias"
          ref={(el) => { sectionRefs.current["tecnologias"] = el; }}
          className="scroll-mt-24"
        >
          <div className="bg-white rounded-card border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                <i className="ri-code-box-line text-secondary-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-900 font-heading">{memorialTechStack.titulo}</h2>
                <p className="text-xs text-foreground-500">Tecnologias, frameworks e versões utilizadas</p>
              </div>
            </div>

            <div className="space-y-6">
              {memorialTechStack.categorias.map((cat) => (
                <div key={cat.categoria}>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
                    <i className="ri-folder-3-line text-secondary-500 text-xs"></i>
                    {cat.categoria}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {cat.itens.map((item) => (
                      <div key={item.nome} className="bg-background-50 rounded-lg p-3 border border-background-200/50 hover:border-primary-200 transition-colors">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-foreground-800">{item.nome}</span>
                          <span className="px-1.5 py-0.5 bg-secondary-100 text-secondary-700 rounded text-[10px] font-semibold">{item.versao}</span>
                        </div>
                        <p className="text-[11px] text-foreground-500 leading-relaxed">{item.descricao}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* === PADRÕES E NORMAS === */}
        <section
          id="padroes"
          ref={(el) => { sectionRefs.current["padroes"] = el; }}
          className="scroll-mt-24"
        >
          <div className="bg-white rounded-card border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-warning-100 flex items-center justify-center">
                <i className="ri-shield-check-line text-warning-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-900 font-heading">Padrões e Normas</h2>
                <p className="text-xs text-foreground-500">Conformidade com padrões internacionais e legislação brasileira</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70">
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wide">Código</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wide">Título</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wide hidden md:table-cell">Descrição</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {memorialStandards.map((std) => (
                    <tr key={std.codigo} className="hover:bg-background-50/50 transition-colors">
                      <td className="py-3 px-3">
                        <span className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs font-semibold whitespace-nowrap">{std.codigo}</span>
                      </td>
                      <td className="py-3 px-3 text-sm font-medium text-foreground-800">{std.titulo}</td>
                      <td className="py-3 px-3 text-xs text-foreground-500 hidden md:table-cell max-w-xs">{std.descricao}</td>
                      <td className="py-3 px-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                          std.status === "Adotado" ? "bg-success-100 text-success-700" :
                          std.status === "Suportado" ? "bg-info-100 text-info-700" :
                          std.status === "Em adaptação" ? "bg-warning-100 text-warning-700" :
                          "bg-secondary-100 text-secondary-700"
                        }`}>
                          {std.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* === GLOSSÁRIO === */}
        <section
          id="glossario"
          ref={(el) => { sectionRefs.current["glossario"] = el; }}
          className="scroll-mt-24"
        >
          <div className="bg-white rounded-card border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-book-open-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-900 font-heading">Glossário Técnico</h2>
                <p className="text-xs text-foreground-500">{memorialGlossary.length} termos definidos — busque por palavra-chave</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="relative max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar termo ou definição..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 bg-background-50"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {filteredGlossary.map((g) => (
                <div key={g.termo} className="bg-background-50 rounded-lg p-3.5 border border-background-200/50">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">{g.termo}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{g.definicao}</p>
                </div>
              ))}
            </div>
            {filteredGlossary.length === 0 && (
              <div className="text-center py-8 text-foreground-400">
                <i className="ri-book-open-line text-3xl mb-2 block"></i>
                <p className="text-sm">Nenhum termo encontrado para &quot;{searchTerm}&quot;.</p>
              </div>
            )}
          </div>
        </section>

        {/* === VERSIONAMENTO === */}
        <section
          id="releases"
          ref={(el) => { sectionRefs.current["releases"] = el; }}
          className="scroll-mt-24"
        >
          <div className="bg-white rounded-card border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-success-100 flex items-center justify-center">
                <i className="ri-git-commit-line text-success-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-900 font-heading">Versionamento e Releases</h2>
                <p className="text-xs text-foreground-500">Histórico de versões, milestones e roadmap</p>
              </div>
            </div>

            <div className="space-y-3">
              {memorialReleases.map((rel) => (
                <div key={rel.versao} className="border border-background-200/70 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedRelease(expandedRelease === rel.versao ? null : rel.versao)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-background-50/50 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-md bg-primary-50 flex items-center justify-center shrink-0">
                      <i className="ri-price-tag-3-line text-primary-600 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-foreground-800">{rel.versao}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                          rel.status === "Em desenvolvimento" ? "bg-primary-100 text-primary-700" :
                          rel.status === "Planejado" ? "bg-secondary-100 text-secondary-700" :
                          "bg-success-100 text-success-700"
                        }`}>
                          {rel.status}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500">{rel.data} • {rel.milestones.length} milestones</p>
                    </div>
                    <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${expandedRelease === rel.versao ? "rotate-180" : ""}`}></i>
                  </button>
                  {expandedRelease === rel.versao && (
                    <div className="px-4 pb-4 pt-0 slide-up">
                      <ul className="space-y-1.5">
                        {rel.milestones.map((m) => (
                          <li key={m} className="flex items-start gap-2 text-xs text-foreground-600">
                            <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 shrink-0"></i>
                            <span>{m}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Sidebar TOC (desktop only) */}
      <aside className="hidden xl:block w-64 shrink-0">
        <div className="sticky top-24 bg-white rounded-card border border-background-200/70 p-4">
          <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">Navegação</p>
          <nav className="space-y-0.5">
            {sections.map((sec) => (
              <button
                key={sec.id}
                onClick={() => scrollToSection(sec.id)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-xs font-medium transition-colors cursor-pointer ${
                  activeSection === sec.id
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-600 hover:bg-background-50 hover:text-foreground-800"
                }`}
              >
                <i className={`${sec.icon} text-sm ${activeSection === sec.id ? "text-primary-500" : "text-foreground-400"}`}></i>
                {sec.label}
              </button>
            ))}
          </nav>
          <div className="mt-4 pt-3 border-t border-background-200/50">
            <p className="text-[10px] text-foreground-400 leading-relaxed">
              {memorialOverview.titulo}<br />
              {memorialOverview.versao} — {memorialOverview.dataAtualizacao}
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}